import * as logger from 'loglevel';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
import transformRequestAirport from './transformRequest/transformAirport';

/**
 * Transforming data from airports api
 * @param {*} propertysRawData
 */
const transformData = (propertysRawData) => {
  const propertys = [];
  for (let index = 0; index < propertysRawData.length; index += 1) {
    const item = propertysRawData[index];
    if (!item.property?.locationLabel) {
      item.label = item.property.name || 'NO_NAME_FROM_API';
    }
    const property = prepareProperty(item);
    propertys.push(property);
  }
  return propertys;
};

const prepareProperty = (item) => {
  const property = {
    value: item.code,
    search: item.property.locationLabel,
    label: item.property.locationLabel,
    name: item.property.name,
    ...item
  };

  return property;
};

const transformDataMultiAggregation = (responseRaw) => {
  const options = [];

  responseRaw.forEach((continent) => {
    continent.countries.forEach((country) => {
      options.push({
        value: country.geoId,
        search: country.locationLabel,
        label: `${country.name}`,
        name: country.name,
        icon: 'country',
        type: 'country'
      });
      country.states.forEach((state) => {
        // options.push({
        //   value: state.geoId,
        //   search: state.locationLabel,
        //   label: state.locationLabel,
        //   name: state.name,
        //   type: "state"
        // });
        state.cities.forEach((city) => {
          options.push({
            value: city.geoId,
            search: city.locationLabel,
            label: `${city.name}`,
            name: city.name,
            icon: 'city',
            type: 'city',
            secondaryLabel: `${state.name}, ${country.name}`
          });
          city.properties.forEach((property) => {
            options.push({
              value: property.geoId,
              search: property.locationLabel,
              label: `${property.locationLabel}`,
              name: property.name,
              icon: 'property',
              type: 'property',
              secondaryLabel: `${state.name}, ${country.name}`
            });
          });
        });
      });
    });
  });

  return options;
};

/**
 * Makes sure the airport response is complain with a basic schema
 * @param {*} property
 */
const validatePropertyMultiAggregationResponse = (responseRaw) => {
  const validation = { isValid: true, message: '' };
  if (!responseRaw || !Array.isArray(responseRaw)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: responseRaw,
      message: 'INTEGRATION__PROPERTY: property must be an Array'
    };
  }
  if (responseRaw.length > 0) {
    const continent = responseRaw[0];
    const country = continent.countries[0];
    const state = country.states[0];
    const city = state.cities[0];
    const property = city.properties[0];

    if (
      !property.locationLabel ||
      !property.geoId ||
      !city.locationLabel ||
      !city.geoId ||
      !state.locationLabel ||
      !state.geoId ||
      !country.locationLabel ||
      !country.geoId ||
      !continent.locationLabel ||
      !continent.geoId
    ) {
      return {
        isValid: false,
        data: property,
        message:
          'INTEGRATION__PROPERTY: property SCHEMA is invalid, please check Airports API documentation.'
      };
    }
  }
  return validation;
};

/**
 * Makes sure the airport response is complain with a basic schema
 * @param {*} property
 */
const validatePropertyResponse = (responseRaw) => {
  const validation = { isValid: true, message: '' };
  if (!responseRaw || !Array.isArray(responseRaw)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: responseRaw,
      message: 'INTEGRATION__PROPERTY: property must be an Array'
    };
  }
  if (responseRaw.length > 0) {
    const property = responseRaw[0];
    if (!property.property.locationLabel) {
      return {
        isValid: false,
        data: property,
        message:
          'INTEGRATION__PROPERTY: property SCHEMA is invalid, please check Airports API documentation.'
      };
    }
  }
  return validation;
};

const getPropertiesMultiAggregation = (location, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__PROPERTY: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1HospitalitySearchAggregation: airportApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = airportApiSettings;

    if (
      !airportApiSettings ||
      !airportApiSettings.method ||
      !airportApiSettings.endPoint ||
      !airportApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__PROPERTY: **integrationSettings.airportApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    axiosWrapper(
      {
        method: airportApiSettings.method,
        url: `${airportApiSettings.endPoint}`,
        headers: { ...airportApiSettings.headers },
        data: transformRequestAirport(location)
      },
      {
        resource: 'INTEGRATION__AIRPORT',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validatePropertyMultiAggregationResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__PROPERTY: Successful request', response.data);
          const options = transformDataMultiAggregation(response.data);
          resolve(options);
        } else {
          logger.info('INTEGRATION__PROPERTY: Missing Data', validation.data);
          reject(validation.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

/**
 * Retrieve airports from the airports Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getProperties = (location, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__PROPERTY: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1HospitalitySearch: airportApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = airportApiSettings;

    if (
      !airportApiSettings ||
      !airportApiSettings.method ||
      !airportApiSettings.endPoint ||
      !airportApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__PROPERTY: **integrationSettings.airportApiSettings** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    axiosWrapper(
      {
        method: airportApiSettings.method,
        url: `${airportApiSettings.endPoint}`,
        headers: { ...airportApiSettings.headers },
        data: transformRequestAirport(location)
      },
      {
        resource: 'INTEGRATION__AIRPORT',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validatePropertyResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__PROPERTY: Successful request', response.data);
          const airports = transformData(response.data);
          resolve(airports);
        } else {
          logger.info('INTEGRATION__PROPERTY: Missing Data', validation.data);
          reject(validation.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getProperties, getPropertiesMultiAggregation, transformData };
